// MessagesUnreadContext — one app-wide source of truth for Chatrone Messages
// notifications. Mounted once in Layout so the unread badge (Sidebar /
// BlockTabBar / MobileLayout) and the live in-app pop-up share a single set of
// Realtime subscriptions instead of each nav surface opening its own.
//
// In-app pop-up rules (toast + sound + opportunistic desktop notification):
//   • any direct message (incl. Janet → you)
//   • any Space message (General + other spaces)
//   • any message that @mentions you (incl. record threads)
//   record-thread chatter only pops when you're @mentioned.
// Suppressed for your own messages and for the thread you're currently viewing.
//
// NOTE: this widening is IN-APP only. Off-app push/email (/api/notify-message)
// stays high-signal — DMs + @mentions — so General chatter never buzzes your
// phone while you're away.

import { createContext, useContext, useEffect, useMemo, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useId } from 'react'
import { supabase } from '../lib/supabase'
import { IS_DEMO } from '../lib/demoClient'
import { useToast } from './ToastContext'
import { shouldPop, NOTIFY_PREF_COLUMNS, NOTIFY_PREFS_EVENT } from '../lib/notifyPrefs'
import { inDesktopShell, desktopNotifyOn, showDesktopNotification } from '../lib/desktopNotify'
import { useThreads } from '../hooks/useMessages'

const MessagesUnreadContext = createContext({ unread: 0 })

export function useMessagesUnread() {
  return useContext(MessagesUnreadContext)
}

// Soft two-note "ding" via Web Audio — no asset file needed. Silently no-ops if
// the browser blocks audio (e.g. before any user gesture).
let _audioCtx = null
function playPing() {
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return
    _audioCtx = _audioCtx || new Ctx()
    const ctx = _audioCtx
    if (ctx.state === 'suspended') ctx.resume()
    const now = ctx.currentTime
    ;[880, 1175].forEach((freq, i) => {
      const o = ctx.createOscillator()
      const g = ctx.createGain()
      o.type = 'sine'
      o.frequency.value = freq
      o.connect(g); g.connect(ctx.destination)
      const t = now + i * 0.12
      g.gain.setValueAtTime(0.0001, t)
      g.gain.exponentialRampToValueAtTime(0.06, t + 0.01)
      g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18)
      o.start(t); o.stop(t + 0.2)
    })
  } catch { /* audio unavailable */ }
}

// Look up a thread that isn't in my cached list (e.g. the first message of a
// brand-new DM) and decide whether I'm party to it. Spaces are team-wide; DMs
// and private spaces require my participation. Returns the thread row or null.
async function resolveVisibleThread(threadId, userId) {
  try {
    const { data: t } = await supabase
      .from('message_threads')
      .select('id, kind, name, subject_label, is_private')
      .eq('id', threadId).maybeSingle()
    if (!t) return null
    if (t.kind === 'dm' || (t.kind === 'space' && t.is_private)) {
      const { data: part } = await supabase
        .from('thread_participants')
        .select('user_id').eq('thread_id', threadId).eq('user_id', userId).maybeSingle()
      if (!part) return null
    }
    return t
  } catch {
    return null
  }
}

export function MessagesUnreadProvider({ userId, children }) {
  const { threads } = useThreads(userId)
  const toast = useToast()
  const navigate = useNavigate()
  const channelId = useId()

  // Keep a live map of my threads so the pop-up handler can classify an incoming
  // message (DM? space? am I a participant?) without an extra query.
  const threadsRef = useRef({})
  useEffect(() => {
    threadsRef.current = Object.fromEntries(threads.map(t => [t.id, t]))
  }, [threads])

  const unread = useMemo(
    () => threads.filter(t => t.unread && !t.muted).length,
    [threads],
  )

  // The "Notify me about" switches. Read once, then kept current by the
  // Settings panel's change event so a flipped switch applies immediately.
  const prefsRef = useRef({})
  useEffect(() => {
    if (!userId) return
    let alive = true
    supabase.from('user_profiles').select(NOTIFY_PREF_COLUMNS).eq('id', userId).maybeSingle()
      .then(({ data, error }) => {
        if (error) console.warn('[messages-notify] could not read notification switches:', error.message)
        if (alive && data) prefsRef.current = { ...prefsRef.current, ...data }
      }, () => {})
    const onChange = (e) => { prefsRef.current = { ...prefsRef.current, ...(e.detail || {}) } }
    window.addEventListener(NOTIFY_PREFS_EVENT, onChange)
    return () => { alive = false; window.removeEventListener(NOTIFY_PREFS_EVENT, onChange) }
  }, [userId])

  // Desktop shell only: mirror the unread count onto the macOS dock badge.
  useEffect(() => {
    window.chatroneDesktop?.setBadge?.(unread)
  }, [unread])

  // Activity heartbeat — stamps user_profiles.last_active_at while the app is
  // open/focused. The email-fallback channel only emails you when this shows
  // you've been away longer than your threshold, so active users never get email.
  // Skipped entirely in the demo: the isolated demo DB's RLS correctly denies
  // this UPDATE for every visitor sharing the one seeded account (403 /
  // 42501), so there is nothing to stamp — same reasoning as every other
  // IS_DEMO-gated write in this codebase (demoClient.js's own header).
  useEffect(() => {
    if (!userId || IS_DEMO) return
    let alive = true
    const beat = () => {
      if (!alive || document.hidden) return
      supabase.from('user_profiles').update({ last_active_at: new Date().toISOString() }).eq('id', userId).then(() => {}, () => {})
    }
    beat()
    const onVis = () => { if (!document.hidden) beat() }
    document.addEventListener('visibilitychange', onVis)
    window.addEventListener('focus', onVis)
    const h = setInterval(beat, 120_000) // every 2 min while open
    return () => { alive = false; clearInterval(h); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', onVis) }
  }, [userId])

  // Live pop-up (toast + sound + desktop notification) on a relevant new message.
  useEffect(() => {
    if (!userId) return
    const channel = supabase
      .channel(`messages-notify-${channelId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'messages' }, async ({ new: msg }) => {
        if (!msg || msg.sender_user_id === userId) return // my own message

        // Resolve the thread. Cached threads are already filtered to ones I can
        // see; an UNKNOWN thread (the first message of a brand-new DM) is looked
        // up live so a first-time DM still pops.
        let thread = threadsRef.current[msg.thread_id]
        if (!thread) {
          thread = await resolveVisibleThread(msg.thread_id, userId)
          if (!thread) return // not a thread I'm party to
        }

        const mentioned = Array.isArray(msg.mentions) && msg.mentions.includes(userId)
        // Pop for DMs I'm in, any Space (incl. General), and @mentions anywhere,
        // as the Settings switches allow. Record-thread chatter stays quiet
        // unless I'm @mentioned.
        if (!shouldPop({ kind: thread.kind, mentioned, fromJanet: msg.sender_type === 'janet' }, prefsRef.current)) return

        // Suppress if I'm already looking at this exact thread.
        const params = new URLSearchParams(window.location.search)
        if (window.location.pathname === '/messages' && params.get('thread') === msg.thread_id) return

        const who = msg.sender_type === 'janet' ? 'Janet' : (msg.sender_name || 'New message')
        const label = thread.kind === 'space' ? `#${thread.name || 'space'} · ${who}` : who
        const preview = (msg.body || '').slice(0, 120)

        playPing()
        toast.action(`💬 ${label}: ${preview}`, 'Open', () => navigate(`/messages?thread=${msg.thread_id}`), 9000)

        // Native notification. In the Mac app it follows the Settings switch
        // (desktopNotify.js); in a browser only if permission was already
        // granted — prompting belongs to the web-push opt-in flow.
        try {
          const shellOk = inDesktopShell() ? desktopNotifyOn()
            : (typeof Notification !== 'undefined' && Notification.permission === 'granted')
          if (shellOk) {
            const n = showDesktopNotification(label, {
              body: preview, tag: msg.thread_id,
              onClick: () => navigate(`/messages?thread=${msg.thread_id}`),
            })
            setTimeout(() => n.close(), 8000)
          }
        } catch { /* notifications unavailable */ }
      })
      .subscribe()
    return () => { supabase.removeChannel(channel) }
  }, [userId, channelId, toast, navigate])

  const value = useMemo(() => ({ unread }), [unread])
  return <MessagesUnreadContext.Provider value={value}>{children}</MessagesUnreadContext.Provider>
}
